import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type {
  Hizmet, ParaBirimi, Proforma as ProformaBelge, ProformaGirdi, ProformaOzet, Urun,
} from '@nero/shared';
import { ANA_PARA_BIRIMI, bugun, kurus, kuruBul, para, tarihGoster } from '@nero/shared';
import { api, useEylem, useVeri } from '../lib/veri.js';
import { useKur } from '../lib/kur.js';
import { oturumBasligi } from '../lib/api.js';
import { useOturum } from '../lib/oturum.js';
import { useBildirim } from '../lib/bildirim.js';
import { BosDurum, HataKutusu, Kart, KartBaslik, SayfaBaslik, UyariSerit, Yukleniyor } from '../bilesenler/temel.js';
import { Girdi, MetinAlani, ParaGirdi, Secim, SecenekSerit } from '../bilesenler/form.js';
import { OnayPenceresi } from '../bilesenler/Pencere.js';
import { MusteriSecici } from '../bilesenler/MusteriSecici.js';
import { Simge } from '../bilesenler/Simge.js';
import { NeroLogo } from '../bilesenler/Logo.js';

/**
 * PROFORMA
 *
 * Nero Kasa'daki proforma belgesi. Bir iş emrinden tek tuşla üretilebilir
 * (müşteri, araç, yapılan işler ve fiyatlar otomatik dolar) ya da elle
 * oluşturulur. "PDF indir" sunucuda üretilen gerçek A4 PDF dosyasını indirir
 * (tarayıcının yazdırma penceresi gerekmez); "Yazdır" ise ekrandaki belgeyi
 * doğrudan yazıcıya gönderir.
 */

/* Kasa ile proforma aynı işin iki yüzü: menüyü büyütmemek için sekmeyle geçilir. */
export function KasaSekmeleri({ etkin }: { etkin: 'kasa' | 'proforma' }) {
  const git = useNavigate();
  return (
    <div className="satir" style={{ marginBottom: 'var(--b4)' }}>
      <SecenekSerit
        deger={etkin}
        secenekler={[
          { deger: 'kasa' as const, ad: 'Kasa hareketleri' },
          { deger: 'proforma' as const, ad: 'Proforma' },
        ]}
        onChange={(d) => git(d === 'kasa' ? '/kasa' : '/proforma')}
      />
    </div>
  );
}

/* ---------- Liste ---------- */

export function Proforma() {
  const { yetkili } = useOturum();
  const { veri, yukleniyor, hata, yenile } = useVeri<ProformaOzet[]>('/proformalar');
  const [silinecek, setSilinecek] = useState<ProformaOzet | null>(null);
  const bildir = useBildirim();

  const sil = useEylem(async (id: number) => {
    await api.sil(`/proformalar/${id}`);
    setSilinecek(null);
    yenile();
    bildir('Proforma çöpe taşındı');
  });

  return (
    <div className="sayfa">
      <SayfaBaslik baslik="Proforma" alt="Teklif ve ön fatura belgeleri">
        {yetkili('proforma.duzenle') && (
          <Link className="btn btn-birincil" to="/proforma/yeni">
            <Simge ad="arti" className="simge" /> Yeni proforma
          </Link>
        )}
      </SayfaBaslik>

      <KasaSekmeleri etkin="proforma" />

      <Kart>
        {hata ? <div className="kart-govde"><HataKutusu hata={hata} yenile={yenile} /></div>
          : yukleniyor ? <div className="kart-govde"><Yukleniyor /></div>
          : !veri || veri.length === 0 ? (
            <BosDurum
              simge="kasa" baslik="Henüz proforma yok"
              aciklama="Bir iş emrinin detayından ya da buradan yeni belge oluşturabilirsiniz."
            />
          ) : (
            <div className="tablo-sarma">
              <table className="tablo">
                <thead>
                  <tr>
                    <th>Belge No</th><th>Tarih</th><th>Müşteri</th><th>Araç</th>
                    <th className="sag">Tutar</th><th className="sag">Kalan</th><th />
                  </tr>
                </thead>
                <tbody>
                  {veri.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <Link to={`/proforma/${p.id}`} className="ana" style={{ textDecoration: 'underline' }}>
                          {p.belge_no}
                        </Link>
                        {p.is_emri_no && (
                          <div className="ikincil">
                            <Link to={`/is-emirleri/${p.is_emri_id}`}>{p.is_emri_no}</Link>
                          </div>
                        )}
                      </td>
                      <td className="ikincil dar">{tarihGoster(p.tarih)}</td>
                      <td>{p.musteri_adi}</td>
                      <td className="ikincil">{p.arac || '—'}</td>
                      <td className="sag para">{para(p.toplam)}</td>
                      <td className="sag para" style={{ color: p.kalan > 0 ? 'var(--uyari)' : 'var(--olumlu)' }}>
                        {para(p.kalan)}
                      </td>
                      <td className="dar">
                        {yetkili('proforma.duzenle') && (
                          <button className="btn btn-sade btn-simge btn-kucuk" aria-label="Sil"
                            onClick={() => setSilinecek(p)}>
                            <Simge ad="cop" boyut={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Kart>

      {silinecek && (
        <OnayPenceresi
          baslik="Proformayı sil"
          aciklama={`${silinecek.belge_no} numaralı belge çöp kutusuna taşınacak.`}
          onayMetni="Sil"
          calisiyor={sil.calisiyor}
          hata={sil.hata}
          kapat={() => setSilinecek(null)}
          onayla={() => void sil.yurut(silinecek.id)}
        />
      )}
    </div>
  );
}

/* ---------- Belge görünümü (A4) ---------- */

function BelgeGorunumu({ b }: { b: ProformaGirdi & { belge_no: string } }) {
  const toplam = kurus(b.kalemler.reduce((t, k) => t + k.adet * k.birim_fiyat, 0));
  const kalan = kurus(toplam - b.odenen);
  return (
    <div className="belge-sarma">
      <div className="belge">
        <div className="belge-ust">
          <div>
            {/* Antette logo; firma adı yalnızca varsayılandan farklıysa
                ayrıca yazılır — aynı adı iki kez göstermenin anlamı yok. */}
            <div className="belge-logo"><NeroLogo yukseklik={40} baslik="" /></div>
            {b.firma_adi && b.firma_adi.toLocaleUpperCase('tr-TR') !== 'NERO PERFORMANCE' && (
              <div className="belge-firma-alt">{b.firma_adi}</div>
            )}
            {b.iletisim && <div className="belge-iletisim">{b.iletisim}</div>}
          </div>
          <div>
            <div className="belge-baslik">{b.belge_basligi || 'PROFORMA FATURA'}</div>
            <div className="belge-no">
              Belge No: {b.belge_no || '—'}<br />
              Tarih: {tarihGoster(b.tarih)}
            </div>
          </div>
        </div>

        <div className="belge-taraf">
          <div className="belge-kutu">
            <h4>Müşteri</h4>
            <div style={{ fontWeight: 700 }}>{b.musteri_adi || '—'}</div>
            {b.telefon && <div className="satir-veri"><span>Telefon</span><span>{b.telefon}</span></div>}
          </div>
          <div className="belge-kutu">
            <h4>Araç</h4>
            <div style={{ fontWeight: 700 }}>{b.arac || '—'}</div>
            {b.yetkili && <div className="satir-veri"><span>Yetkili</span><span>{b.yetkili}</span></div>}
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th style={{ width: '8mm' }}>#</th>
              <th>Hizmet / Ürün Açıklaması</th>
              <th className="sag" style={{ width: '18mm' }}>Adet</th>
              <th className="sag" style={{ width: '30mm' }}>Birim</th>
              <th className="sag" style={{ width: '32mm' }}>Tutar</th>
            </tr>
          </thead>
          <tbody>
            {b.kalemler.map((k, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>{k.aciklama}</td>
                <td className="sag para">{k.adet}</td>
                <td className="sag para">{para(k.birim_fiyat)}</td>
                <td className="sag para">{para(kurus(k.adet * k.birim_fiyat))}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="belge-toplam">
          <div className="satir-veri genel"><span>GENEL TOPLAM</span><span className="para">{para(toplam)}</span></div>
          <div className="satir-veri"><span>Ödenen</span><span className="para">{para(b.odenen)}</span></div>
          <div className="satir-veri kalan"><span>KALAN</span><span className="para">{para(kalan)}</span></div>
        </div>

        {(b.banka_adi || b.iban) && (
          <div className="belge-banka">
            <strong>BANKA HESAP BİLGİLERİ</strong>
            <div>{b.banka_adi}</div>
            <div style={{ letterSpacing: '0.5px' }}>{b.iban}</div>
          </div>
        )}

        {b.alt_aciklama && <div className="belge-aciklama">{b.alt_aciklama}</div>}

        <div className="belge-alt">
          <span>{b.firma_adi || 'NERO PERFORMANCE'}</span>
          <span>{b.yetkili ? `Yetkili: ${b.yetkili}` : ''}</span>
        </div>
      </div>
    </div>
  );
}

/* ---------- Düzenleme ve belge ekranı ---------- */

const BOS_KALEM = {
  aciklama: '', adet: 1, birim_fiyat: 0,
  urun_id: null as number | null, hizmet_id: null as number | null,
  /* Kalem başına para birimi. Teklifin kuru yazıldığı anda donar:
     müşteriye verilen rakam kur oynadı diye değişmez. */
  para_birimi: ANA_PARA_BIRIMI as ParaBirimi,
  /* Kayıtlı bir belgeden gelirse kalemin DONMUŞ kuru; yeni kalemde null
     ve sunucu günün kurunu uygular. */
  kur: null as number | null,
};

export function ProformaDetayi() {
  const { id } = useParams();
  const [parametre] = useSearchParams();
  const git = useNavigate();
  const bildir = useBildirim();
  const { yetkili } = useOturum();
  const yeniMi = !id || id === 'yeni';
  const isEmriId = parametre.get('is_emri') ?? '';

  const taslakYolu = yeniMi ? `/proformalar/taslak${isEmriId ? `?is_emri_id=${isEmriId}` : ''}` : null;
  const taslak = useVeri<Partial<ProformaGirdi>>(taslakYolu);
  const mevcut = useVeri<ProformaBelge>(yeniMi ? null : `/proformalar/${id}`);

  const [form, setForm] = useState<ProformaGirdi & { belge_no: string }>({
    belge_no: '', belge_basligi: 'PROFORMA FATURA', tarih: bugun(),
    is_emri_id: null, musteri_id: null, musteri_adi: '', telefon: '', arac: '',
    firma_adi: '', yetkili: '', banka_adi: '', iban: '', iletisim: '', alt_aciklama: '',
    odenen: 0, kalemler: [{ ...BOS_KALEM }],
  });
  const [duzenle, setDuzenle] = useState(yeniMi);

  /* Sunucudan gelen taslak veya kayıtlı belge forma yüklenir. */
  useEffect(() => {
    const k = taslak.veri;
    if (!yeniMi || !k) return;
    setForm((f) => ({
      ...f, ...k,
      belge_no: k.belge_no ?? '',
      tarih: k.tarih ?? bugun(),
      kalemler: k.kalemler && k.kalemler.length ? k.kalemler : [{ ...BOS_KALEM }],
    }));
  }, [taslak.veri, yeniMi]);

  useEffect(() => {
    const b = mevcut.veri;
    if (yeniMi || !b) return;
    setForm({
      belge_no: b.belge_no, belge_basligi: b.belge_basligi, tarih: b.tarih.slice(0, 10),
      is_emri_id: b.is_emri_id, musteri_id: b.musteri_id, musteri_adi: b.musteri_adi,
      telefon: b.telefon, arac: b.arac, firma_adi: b.firma_adi, yetkili: b.yetkili,
      banka_adi: b.banka_adi, iban: b.iban, iletisim: b.iletisim, alt_aciklama: b.alt_aciklama,
      odenen: b.odenen,
      kalemler: b.kalemler.length
        /* KAYITLI BELGE AÇILIRKEN YAZILAN FİYAT GERİ GELİR, TL karşılığı
           DEĞİL. Aksi hâlde "1.000 €" diye yazılmış bir teklifi açınca
           50.000 görünür ve kaydedince bir kez daha kurla çarpılırdı.
           Kur da taşınır: verilmiş teklifin rakamı sabit kalsın. */
        ? b.kalemler.map((k) => ({
          aciklama: k.aciklama, adet: k.adet,
          birim_fiyat: k.doviz_birim_fiyat ?? k.birim_fiyat,
          para_birimi: k.para_birimi ?? ANA_PARA_BIRIMI,
          kur: k.kur ?? null,
          urun_id: k.urun_id ?? null, hizmet_id: k.hizmet_id ?? null,
        }))
        : [{ ...BOS_KALEM }],
    });
    setDuzenle(false);
  }, [mevcut.veri, yeniMi]);

  /**
   * GENEL TOPLAM HER ZAMAN TL'DİR.
   *
   * Kalemler farklı para birimlerinde olabilir; "300 € + 1.000 ₺" diye bir
   * toplam yoktur. Her kalem kendi kuruyla TL'ye çevrilip toplanır —
   * sunucunun yaptığı hesabın aynısı (bkz. depo/proformalar.ts).
   * Kur bilinmiyorsa kalem TL sayılır; sunucu zaten kendi kuruyla yeniden
   * hesaplayacağı için bu yalnız ÖNİZLEMEYİ etkiler, kaydı değil.
   */
  const kurKaydi = useKur();
  const toplam = useMemo(
    () => kurus(form.kalemler.reduce((t, k) => {
      const kur = k.kur ?? kuruBul(kurKaydi, k.para_birimi) ?? 1;
      return t + Number(k.adet || 0) * Number(k.birim_fiyat || 0) * kur;
    }, 0)),
    [form.kalemler, kurKaydi],
  );

  const alan = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));
  const kalemAlan = (i: number, yama: Partial<typeof BOS_KALEM>) =>
    setForm((f) => ({ ...f, kalemler: f.kalemler.map((k, j) => (j === i ? { ...k, ...yama } : k)) }));

  /* Stoktaki ürünler ve katalogdaki hizmetler kalemlere hazır gelsin. */
  const urunler = useVeri<Urun[]>('/urunler?pasif=0');
  const hizmetler = useVeri<Hizmet[]>('/hizmetler');

  /**
   * Kalem için ürün/hizmet seçimi. Açıklama ve birim fiyat otomatik dolar.
   * Proforma bir TEKLİF olduğu için stoktan düşme YAPILMAZ; bağlantı
   * yalnızca "bu kalem hangi kayıttan geldi" izini tutar.
   */
  function kaynakSec(i: number, deger: string) {
    if (!deger) { kalemAlan(i, { urun_id: null, hizmet_id: null }); return; }
    const [tur, kimlikMetni] = deger.split(':');
    const kimlik = Number(kimlikMetni);
    if (tur === 'urun') {
      const u = urunler.veri?.find((x) => x.id === kimlik);
      if (!u) return;
      /* Ürün kendi para biriminde gelir: 150 €'ya fiyatlanmış parça
         teklife de "150 €" diye düşer, TL karşılığı olarak değil. */
      kalemAlan(i, {
        urun_id: u.id, hizmet_id: null, aciklama: u.ad,
        birim_fiyat: Number(u.satis_doviz ?? u.satis_fiyat) || 0,
        para_birimi: u.satis_para_birimi ?? ANA_PARA_BIRIMI,
      });
      return;
    }
    const h = hizmetler.veri?.find((x) => x.id === kimlik);
    if (!h) return;
    /* Hizmet fiyatları TL'dir; para birimi de TL'ye çekilir ki € seçiliyken
       TL bir rakam yazılıp teklif 50 katına çıkmasın. */
    kalemAlan(i, {
      hizmet_id: h.id, urun_id: null, aciklama: h.ad,
      birim_fiyat: Number(h.varsayilan_fiyat) || 0,
      para_birimi: ANA_PARA_BIRIMI,
    });
  }

  const kaynakSecenekleri = useMemo(() => [
    ...(hizmetler.veri ?? []).filter((h) => h.aktif !== 0)
      .map((h) => ({ deger: `hizmet:${h.id}`, ad: `Hizmet · ${h.ad}` })),
    ...(urunler.veri ?? []).map((u) => ({
      deger: `urun:${u.id}`,
      ad: `Stok · ${u.ad}${u.adet > 0 ? ` (${u.adet} ${u.birim})` : ' (tükendi)'}`,
    })),
  ], [hizmetler.veri, urunler.veri]);

  /* Gerçek PDF sunucuda üretilir ve dosya olarak iner. */
  const pdfIndir = useEylem(async () => {
    const yanit = await fetch(`/api/proformalar/${id}/pdf`, {
      credentials: 'same-origin', headers: oturumBasligi(),
    });
    if (!yanit.ok) throw new Error('PDF üretilemedi. Sunucu günlüğünü kontrol edin.');
    const blob = await yanit.blob();
    const adBasligi = yanit.headers.get('content-disposition') ?? '';
    const eslesme = /filename="([^"]+)"/.exec(adBasligi);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = eslesme?.[1] ?? `${form.belge_no || 'proforma'}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    bildir('PDF indirildi');
  });

  const kaydet = useEylem(async () => {
    const govde = { ...form, kalemler: form.kalemler.filter((k) => k.aciklama.trim()) };
    if (!govde.kalemler.length) throw new Error('En az bir kalem girin.');
    if (yeniMi) {
      const b = await api.gonder<ProformaBelge>('/proformalar', govde);
      bildir(`${b.belge_no} oluşturuldu`);
      git(`/proforma/${b.id}`, { replace: true });
    } else {
      await api.guncelle(`/proformalar/${id}`, govde);
      mevcut.yenile();
      setDuzenle(false);
      bildir('Proforma güncellendi');
    }
  });

  if (!yeniMi && mevcut.hata) {
    return <div className="sayfa"><HataKutusu hata={mevcut.hata} yenile={mevcut.yenile} /></div>;
  }
  /* İş emrinden proforma açılırken taslak gelmezse kullanıcı bomboş bir form
     görüp elle doldurmaya başlıyordu; hata artık açıkça gösteriliyor. */
  if (yeniMi && taslak.hata) {
    return <div className="sayfa"><HataKutusu hata={taslak.hata} yenile={taslak.yenile} /></div>;
  }
  if ((!yeniMi && mevcut.yukleniyor) || (yeniMi && taslak.yukleniyor)) {
    return <div className="sayfa"><Yukleniyor /></div>;
  }

  return (
    <div className="sayfa" style={{ maxWidth: 1400 }}>
      <div className="yazdirma-gizle">
        <SayfaBaslik
          baslik={yeniMi ? 'Yeni proforma' : form.belge_no}
          alt={yeniMi ? 'Teklif belgesi oluştur' : `${form.musteri_adi}${form.arac ? ` · ${form.arac}` : ''}`}
        >
          <Link className="btn btn-sade" to="/proforma">Listeye dön</Link>
          {yetkili('proforma.duzenle') && !yeniMi && (
            <button className="btn btn-ikincil" onClick={() => setDuzenle((d) => !d)}>
              {duzenle ? 'Düzenlemeyi kapat' : 'Düzenle'}
            </button>
          )}
          {!yeniMi && !duzenle && (
            <>
              <button className="btn btn-sade" onClick={() => window.print()}>
                Yazdır
              </button>
              <button className="btn btn-birincil" onClick={() => void pdfIndir.yurut(undefined as never)}
                disabled={pdfIndir.calisiyor}>
                <Simge ad="indir" className="simge" />
                {pdfIndir.calisiyor ? 'Hazırlanıyor…' : 'PDF indir'}
              </button>
            </>
          )}
          {(yeniMi || duzenle) && (
            <button className="btn btn-birincil" onClick={() => void kaydet.yurut(undefined as never)}
              disabled={kaydet.calisiyor}>
              {kaydet.calisiyor ? 'Kaydediliyor…' : 'Kaydet'}
            </button>
          )}
        </SayfaBaslik>

        {kaydet.hata && <UyariSerit ton="kritik">{kaydet.hata}</UyariSerit>}
        {pdfIndir.hata && <UyariSerit ton="kritik">{pdfIndir.hata}</UyariSerit>}
      </div>

      {(yeniMi || duzenle) ? (
        <div className="izgara" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 'var(--b5)', alignItems: 'start' }}>
          <div className="sutun" style={{ gap: 'var(--b4)' }}>
            <Kart>
              <KartBaslik baslik="Belge" />
              <div className="kart-govde sutun">
                <div className="izgara izgara-2">
                  <Girdi etiket="Belge No" value={form.belge_no} onChange={(v) => alan('belge_no', v)}
                    placeholder="Otomatik" />
                  <Girdi etiket="Tarih" type="date" value={form.tarih} onChange={(v) => alan('tarih', v)} />
                </div>
                <Girdi etiket="Belge başlığı" value={form.belge_basligi} onChange={(v) => alan('belge_basligi', v)} />
              </div>
            </Kart>

            <Kart>
              <KartBaslik baslik="Müşteri ve araç" />
              <div className="kart-govde sutun">
                {/* KAYITLI MÜŞTERİ — İSTEĞE BAĞLI.
                    Teklif kayıtsız birine de verilebilir; o yüzden aşağıdaki
                    alanlar elle doldurulabilir olarak KALIR. Buradan seçmek
                    yalnızca onları doldurur ve belgeyi müşteriye bağlar. */}
                <MusteriSecici
                  deger={form.musteri_id ? String(form.musteri_id) : ''}
                  onChange={(id, m) => setForm((f) => ({
                    ...f,
                    musteri_id: id ? Number(id) : null,
                    /* Ad ve telefon yalnız SEÇİMDE doldurulur; seçim
                       kaldırılınca yazılanlar silinmez — belge elle
                       yazılmış bilgiyle kalmalı. */
                    musteri_adi: m ? m.ad : f.musteri_adi,
                    telefon: m && m.telefon ? m.telefon : f.telefon,
                  }))}
                  etiket="Kayıtlı müşteri"
                  yerTutucu="Ara — isim, telefon veya plaka… (Enter ile seç)"
                  bosMetin="İsteğe bağlı — seçerseniz ad ve telefon kendiliğinden dolar"
                  baslangicAdi={form.musteri_id ? form.musteri_adi : null}
                />
                <Girdi etiket="Müşteri adı" value={form.musteri_adi} onChange={(v) => alan('musteri_adi', v)} required duzen="ozelAd" />
                <div className="izgara izgara-2">
                  <Girdi etiket="Telefon" value={form.telefon} onChange={(v) => alan('telefon', v)} inputMode="tel" duzen="telefon" />
                  <Girdi etiket="Araç" value={form.arac} onChange={(v) => alan('arac', v)} placeholder="34 ABC 123 · BMW 320i" />
                </div>
                {form.is_emri_id && (
                  <div style={{ fontSize: 'var(--p-12)', color: 'var(--metin-3)' }}>
                    Bu belge <Link to={`/is-emirleri/${form.is_emri_id}`} style={{ textDecoration: 'underline' }}>iş emrinden</Link> üretildi.
                  </div>
                )}
              </div>
            </Kart>

            <Kart>
              <KartBaslik baslik="Kalemler">
                <button className="btn btn-sade btn-kucuk"
                  onClick={() => setForm((f) => ({ ...f, kalemler: [...f.kalemler, { ...BOS_KALEM }] }))}>
                  Kalem ekle
                </button>
              </KartBaslik>
              <div className="kart-govde sutun">
                {form.kalemler.map((k, i) => (
                  <div key={i} className="satir" style={{ gap: 'var(--b2)', alignItems: 'flex-end' }}>
                    <div style={{ width: 150 }}>
                      <Secim
                        etiket={i === 0 ? 'Stok / hizmet' : undefined}
                        value={k.urun_id ? `urun:${k.urun_id}` : k.hizmet_id ? `hizmet:${k.hizmet_id}` : ''}
                        onChange={(v) => kaynakSec(i, v)}
                        bosMetin="Serbest kalem"
                        secenekler={kaynakSecenekleri}
                      />
                    </div>
                    <div className="esnek" style={{ minWidth: 140 }}>
                      <Girdi etiket={i === 0 ? 'Açıklama' : undefined} value={k.aciklama}
                        onChange={(v) => kalemAlan(i, { aciklama: v })} placeholder="Stage 1 yazılım" duzen="urunAdi" />
                    </div>
                    <div style={{ width: 74 }}>
                      <Girdi etiket={i === 0 ? 'Adet' : undefined} type="number" min={0} step="0.01"
                        value={k.adet} onChange={(v) => kalemAlan(i, { adet: v === '' ? 1 : Number(v) })} />
                    </div>
                    <div style={{ width: 176 }}>
                      <ParaGirdi
                        etiket={i === 0 ? 'Birim fiyat' : undefined} deger={k.birim_fiyat}
                        onChange={(v) => kalemAlan(i, { birim_fiyat: Number(v) })}
                        paraBirimi={k.para_birimi ?? ANA_PARA_BIRIMI}
                        /* Para birimi değişince kur DA düşürülür: donmuş eski
                           kur yeni para birimine uygulanamaz. */
                        onParaBirimi={(b) => kalemAlan(i, { para_birimi: b, kur: null })}
                      />
                    </div>
                    <button className="btn btn-sade btn-simge btn-kucuk" aria-label="Kalemi sil"
                      style={{ marginBottom: 6 }}
                      disabled={form.kalemler.length <= 1}
                      onClick={() => setForm((f) => ({ ...f, kalemler: f.kalemler.filter((_, j) => j !== i) }))}>
                      <Simge ad="cop" boyut={14} />
                    </button>
                  </div>
                ))}
                <div className="satir" style={{ justifyContent: 'space-between', borderTop: '1px solid var(--cizgi)', paddingTop: 'var(--b3)' }}>
                  <span style={{ color: 'var(--metin-2)' }}>Genel toplam</span>
                  <strong className="para">{para(toplam)}</strong>
                </div>
                <ParaGirdi etiket="Ödenen" deger={form.odenen} onChange={(v) => alan('odenen', Number(v))} />
                <div className="satir" style={{ justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--metin-2)' }}>Kalan</span>
                  <strong className="para" style={{ color: toplam - form.odenen > 0 ? 'var(--uyari)' : 'var(--olumlu)' }}>
                    {para(kurus(toplam - form.odenen))}
                  </strong>
                </div>
              </div>
            </Kart>

            <Kart>
              <KartBaslik baslik="Firma ve banka" />
              <div className="kart-govde sutun">
                <Girdi etiket="Firma adı" value={form.firma_adi} onChange={(v) => alan('firma_adi', v)} duzen="ozelAd" />
                <Girdi etiket="Yetkili" value={form.yetkili} onChange={(v) => alan('yetkili', v)} duzen="ozelAd" />
                <Girdi etiket="Banka adı" value={form.banka_adi} onChange={(v) => alan('banka_adi', v)} duzen="ozelAd" />
                <Girdi etiket="IBAN" value={form.iban} onChange={(v) => alan('iban', v)} placeholder="TR.. .... .... .... .... .... .." duzen="iban" />
                <MetinAlani etiket="İletişim bilgisi" rows={2} value={form.iletisim}
                  onChange={(v) => alan('iletisim', v)} placeholder="Adres · Telefon · E-posta" />
                <MetinAlani etiket="Alt açıklama" rows={3} value={form.alt_aciklama}
                  onChange={(v) => alan('alt_aciklama', v)}
                  placeholder="Teklif 15 gün geçerlidir. Fiyatlara KDV dahildir." />
              </div>
            </Kart>
          </div>

          <div style={{ position: 'sticky', top: 'var(--b4)' }}>
            <div style={{ transform: 'scale(0.62)', transformOrigin: 'top left', width: '161%' }}>
              <BelgeGorunumu b={form} />
            </div>
          </div>
        </div>
      ) : (
        <BelgeGorunumu b={form} />
      )}
    </div>
  );
}
